import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFamily } from '@/api/familyApi';
import { useAttributeGroupsWithUsage } from '@/api/attributeGroupApi';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, PencilIcon, CheckIcon, XIcon } from 'lucide-react';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import ErrorBoundary from '@/components/ErrorBoundary';

export function FamilyDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Get organization settings for date formatting
  const { orgSettings } = useOrgSettings();
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Settings page URL with families tab
  const settingsFamiliesUrl = '/app/settings?tab=families';

  const numericId = id ? parseInt(id, 10) : 0;
  const { data: family, isLoading, isError, error } = useFamily(numericId);
  const { data: attributeGroups } = useAttributeGroupsWithUsage();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-8 w-60" />
          <Skeleton className="h-10 w-20" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-3/4" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <div className="rounded-md border p-4">
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-6">
        <h2 className="mb-2 text-lg font-medium text-red-800">{t('common.messages.error')}</h2>
        <p className="text-red-700">
          {error instanceof Error ? error.message : t('common.messages.unknownError')}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(settingsFamiliesUrl)}>
          <ArrowLeft className="mr-2 size-4" />
          {t('families.form.back')}
        </Button>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="rounded-md border border-yellow-200 bg-yellow-50 p-6">
        <h2 className="mb-2 text-lg font-medium text-yellow-800">
          {t('common.messages.notFound')}
        </h2>
        <p className="text-yellow-700">{t('families.noData')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(settingsFamiliesUrl)}>
          <ArrowLeft className="mr-2 size-4" />
          {t('families.form.back')}
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(settingsFamiliesUrl)}>
              <ArrowLeft className="mr-2 size-4" />
              {t('families.form.back')}
            </Button>
            <h1 className="text-2xl font-bold">{family.label}</h1>
          </div>
          <Button onClick={() => navigate(`/app/products/families/${family.id}/edit`)}>
            <PencilIcon className="mr-2 size-4" />
            {t('actions.edit')}
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <h2 className="mb-2 text-lg font-medium">{t('families.title')}</h2>
              <div className="rounded-md bg-muted/50 p-4">
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="font-medium text-muted-foreground">
                      {t('families.table.headers.code')}:
                    </dt>
                    <dd>{family.code}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium text-muted-foreground">
                      {t('families.table.headers.label')}:
                    </dt>
                    <dd>{family.label}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium text-muted-foreground">
                      {t('families.table.headers.createdAt')}:
                    </dt>
                    <dd>
                      {family.created_at
                        ? formatDateForDisplay(family.created_at, dateFormat)
                        : '-'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="font-medium text-muted-foreground">{t('fields.updatedAt')}:</dt>
                    <dd>
                      {family.updated_at
                        ? formatDateForDisplay(family.updated_at, dateFormat)
                        : '-'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            {family.description && (
              <div>
                <h2 className="mb-2 text-lg font-medium">{t('families.form.description')}</h2>
                <div className="rounded-md bg-muted/50 p-4">
                  <p className="whitespace-pre-wrap">{family.description}</p>
                </div>
              </div>
            )}
          </div>
          <div>
            <h2 className="mb-2 text-lg font-medium">
              {t('families.table.headers.attributeGroups')}
            </h2>
            {family.attribute_groups && family.attribute_groups.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('families.form.group')}</TableHead>
                      <TableHead>{t('families.form.required')}</TableHead>
                      <TableHead>{t('fields.order')}</TableHead>
                      <TableHead>{t('families.table.headers.usage')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {family.attribute_groups
                      ?.sort((a, b) => a.order - b.order)
                      .map(group => {
                        // Find the enhanced group data with usage information
                        const enhancedGroup = attributeGroups?.find(
                          ag => ag.id === group.attribute_group
                        );

                        return (
                          <TableRow key={group.id}>
                            <TableCell className="font-medium">
                              {group.attribute_group_object?.name ||
                                enhancedGroup?.name ||
                                `Group #${group.attribute_group}`}
                            </TableCell>
                            <TableCell>
                              {group.required ? (
                                <CheckIcon className="size-4 text-green-500" />
                              ) : (
                                <XIcon className="size-4 text-enterprise-400" />
                              )}
                            </TableCell>
                            <TableCell>{group.order}</TableCell>
                            <TableCell>
                              {enhancedGroup?.usage ? (
                                <div className="flex flex-wrap gap-1">
                                  <Badge variant="secondary" className="text-xs">
                                    {enhancedGroup.usage.productCount}{' '}
                                    {t('families.usage.products')}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {enhancedGroup.usage.attributeCount} {t('families.usage.attrs')}
                                  </Badge>
                                  {enhancedGroup.usage.hasValues && (
                                    <Badge
                                      variant="secondary"
                                      className="bg-green-50 text-xs text-green-700 dark:bg-green-950/50 dark:text-green-300"
                                    >
                                      {t('families.usage.hasValues')}
                                    </Badge>
                                  )}
                                  {enhancedGroup.usage.familyCount > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs text-purple-600 dark:text-purple-400"
                                    >
                                      {enhancedGroup.usage.familyCount}{' '}
                                      {t('families.usage.families')}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  {t('families.usage.noUsageData')}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="rounded-md border p-8 text-center text-muted-foreground">
                {t('families.form.noAssignedGroups')}
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
